/**
 * A node's address from the first visible tree level. Each segment is a stable
 * sibling ID, not a label or row index: ['document', 'bookmark'] is a child of
 * ['document']. The empty path denotes the implicit root and is not a visible row.
 *
 * Paths are compared segment by segment. Joining with a separator would make
 * IDs containing that separator ambiguous; array identity would make rebuilt
 * paths fail to match. Callers must not mutate paths after storing them.
 */
export type TreePath = readonly string[]

function samePath(left: TreePath, right: TreePath): boolean {
  return left.length == right.length && left.every((segment, index) => segment == right[index])
}

export function isTreePathExpanded(expandedPaths: TreePath[], path: TreePath): boolean {
  return expandedPaths.some((expanded) => samePath(expanded, path))
}

/**
 * Toggle only this node. Keep descendant entries when collapsing a parent so
 * reopening it restores the user's previous choices inside that subtree.
 */
export function toggleTreePath(expandedPaths: TreePath[], path: TreePath): TreePath[] {
  if (isTreePathExpanded(expandedPaths, path)) {
    return expandedPaths.filter((expanded) => !samePath(expanded, path))
  }
  return [...expandedPaths, path]
}

/**
 * Open the target and every visible ancestor, without closing unrelated nodes.
 * Repeated calls are idempotent. An empty path adds nothing.
 *
 * To reveal a leaf, pass its parent's path: expanding ['document'] exposes the
 * child ['document', 'bookmark'] without recording expansion for the leaf itself.
 * The caller owns that distinction; this helper does not inspect tree data.
 */
export function expandTreePath(expandedPaths: TreePath[], path: TreePath): TreePath[] {
  let result = expandedPaths
  for (let depth = 1; depth <= path.length; depth++) {
    const ancestor = path.slice(0, depth)
    if (!isTreePathExpanded(result, ancestor)) result = [...result, ancestor]
  }
  return result
}
